import { Skeleton } from './skeleton';
import { cn } from '@/lib/utils';

interface ContentSkeletonProps {
  variant?: 'card' | 'list' | 'page' | 'dynamic' | 'inline';
  rows?: number;
  className?: string;
}

function CardSkeleton() {
  return (
    <div className="space-y-4 rounded-xl border border-border/70 bg-card p-5">
      <div className="space-y-2">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-8 w-56" />
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <Skeleton className="h-24 w-full rounded-xl" />
        <Skeleton className="h-24 w-full rounded-xl" />
        <Skeleton className="h-24 w-full rounded-xl" />
      </div>
      <Skeleton className="h-40 w-full rounded-xl" />
    </div>
  );
}

function ListSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 rounded-lg border border-border/50 p-3">
          <Skeleton className="h-10 w-10 shrink-0 rounded-full" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-4 w-3/5" />
            <Skeleton className="h-3 w-2/5" />
          </div>
          <Skeleton className="h-8 w-16 shrink-0 rounded-md" />
        </div>
      ))}
    </div>
  );
}

function PageSkeleton() {
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-72" />
      </div>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Skeleton className="h-32 w-full rounded-xl" />
        <Skeleton className="h-32 w-full rounded-xl" />
        <Skeleton className="h-32 w-full rounded-xl" />
      </div>
      <Skeleton className="h-64 w-full rounded-xl" />
    </div>
  );
}

function DynamicSkeleton() {
  return (
    <div className="flex h-full w-full items-center justify-center bg-background p-8">
      <div className="w-full max-w-md space-y-3">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-24 w-full rounded-xl" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    </div>
  );
}

function InlineSkeleton() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="flex items-center gap-3">
        <Skeleton className="h-4 w-4 rounded-full" />
        <Skeleton className="h-4 w-32" />
      </div>
    </div>
  );
}

export function ContentSkeleton({ variant = 'card', rows, className }: ContentSkeletonProps) {
  const Component = {
    card: CardSkeleton,
    list: () => <ListSkeleton rows={rows} />,
    page: PageSkeleton,
    dynamic: DynamicSkeleton,
    inline: InlineSkeleton,
  }[variant];

  return (
    <div className={cn('animate-in fade-in duration-300', className)}>
      <Component />
    </div>
  );
}
